import { LinearProgress, Stack, StackProps, styled } from '@mui/material';

import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';

type DetailsHeaderContainerProps = {
  isLoading?: boolean;
  variant?: 'card' | 'plain';
} & Omit<StackProps, 'direction'>;

export default function DetailsHeaderContainer(
  props: DetailsHeaderContainerProps
) {
  const isMobileViewport = useIsMobileViewport();
  const { isLoading = false, variant = 'card', children, ...rest } = props;

  return (
    <StyledDetails
      id="details-header-container"
      isLoading={isLoading}
      isMobileViewport={isMobileViewport}
      variant={variant}
      {...rest}
    >
      {isLoading ? <LinearProgress /> : children}
    </StyledDetails>
  );
}

function getVariantStyles({
  variant,
  isLoading,
  isMobileViewport
}: {
  variant: 'card' | 'plain';
  isLoading: boolean;
  isMobileViewport: boolean;
}): Record<string, string | number> {
  if (variant === 'plain') {
    return { alignItems: 'stretch', flexDirection: 'column' };
  }

  return {
    borderRadius: isMobileViewport ? '0' : '16px',
    minHeight: 64,
    backgroundColor: 'var(--palette-semantic-background-base)',
    padding: isLoading ? '0' : '8px 24px',
    alignItems: isLoading ? 'flex-start' : 'center',
    overflowY: 'hidden',
    overflowX: 'auto',
    flexDirection: 'row'
  };
}

const StyledDetails = styled(Stack, {
  shouldForwardProp: (prop) =>
    !['isLoading', 'isMobileViewport', 'variant'].includes(prop as string)
})<{
  isLoading: boolean;
  isMobileViewport: boolean;
  variant: 'card' | 'plain';
}>(({ isLoading, isMobileViewport, variant, theme }) => ({
  marginBottom: '16px',
  ...getVariantStyles({ variant, isLoading, isMobileViewport }),
  '.label': {
    color: 'var(--palette-semantic-text-primary)',
    fontWeight: '500',
    fontSize: '14px'
  },
  '.label.placeholderLabel': {
    color: theme.palette.text.secondary
  },
  ' .sub-label': {
    color: 'var(--palette-semantic-text-caption)',
    fontSize: '12px'
  }
}));
